// @intent: Pixi React renderer for renderer-neutral RenderNode trees
// @flow: PixiRuntimeCanvas passes RenderNode root -> PixiLayerRenderer recurses -> pointer events emit layer ids
// @stale: update when RenderNode variants or @pixi/react intrinsic names change

import '@pixi/react';
import { useCallback, useRef } from 'react';
import type { RenderNode } from '@mintyads/playable-api';
import { getPixiTexture } from './pixiAssets';

interface PixiLayerRendererProps {
  node: RenderNode;
  onLayerSelected: (layerId: string | null) => void;
  registerTarget?: ((layerId: string, target: Record<string, unknown>) => () => void) | undefined;
}

export function PixiLayerRenderer({ node, onLayerSelected, registerTarget }: PixiLayerRendererProps) {
  const unregisterTargetRef = useRef<(() => void) | null>(null);
  const targetRef = useCallback((target: unknown) => {
    unregisterTargetRef.current?.();
    unregisterTargetRef.current = null;
    if (target && registerTarget) {
      unregisterTargetRef.current = registerTarget(node.id, target as Record<string, unknown>);
    }
  }, [node.id, registerTarget]);
  const onPointerDown = useCallback(() => {
    onLayerSelected(node.id);
  }, [node.id, onLayerSelected]);

  switch (node.type) {
    case 'container':
      return (
        <pixiContainer ref={targetRef} x={node.x} y={node.y} visible={node.visible}>
          {node.children.map((child) => (
            <PixiLayerRenderer
              key={child.id}
              node={child}
              onLayerSelected={onLayerSelected}
              registerTarget={registerTarget}
            />
          ))}
        </pixiContainer>
      );

    case 'rect': {
      const tex = node.assetUrl ? getPixiTexture(node.assetUrl) : null;
      if (tex) {
        return (
          <pixiSprite
            ref={targetRef}
            texture={tex}
            x={node.x}
            y={node.y}
            width={node.width}
            height={node.height}
            alpha={node.alpha}
            visible={node.visible}
            eventMode="static"
            onPointerDown={onPointerDown}
          />
        );
      }

      return (
        <pixiGraphics
          ref={targetRef}
          x={node.x}
          y={node.y}
          alpha={node.alpha}
          visible={node.visible}
          eventMode="static"
          onPointerDown={onPointerDown}
          draw={(g) => {
            g.clear();
            g.rect(0, 0, node.width, node.height);
            g.fill(node.color);
          }}
        />
      );
    }

    case 'sprite': {
      const tex = getPixiTexture(node.assetUrl);
      if (tex === null) return null;
      return (
        <pixiSprite
          ref={targetRef}
          texture={tex}
          x={node.x}
          y={node.y}
          width={node.width}
          height={node.height}
          tint={node.tint ?? 0xffffff}
          alpha={node.alpha}
          visible={node.visible}
          eventMode="static"
          onPointerDown={onPointerDown}
        />
      );
    }

    case 'text':
      return (
        <pixiText
          ref={targetRef}
          text={node.text}
          x={node.x}
          y={node.y}
          visible={node.visible}
          style={{
            fontFamily: node.fontFamily,
            fontSize: node.fontSize,
            fill: node.color,
          }}
        />
      );

    case 'button': {
      const tex = node.assetUrl ? getPixiTexture(node.assetUrl) : null;
      return (
        <pixiContainer ref={targetRef} x={node.x} y={node.y} visible={node.visible} eventMode="static" onPointerDown={onPointerDown}>
          <pixiGraphics
            draw={(g) => {
              g.clear();
              g.roundRect(0, 0, node.width, node.height, 12);
              g.fill(node.color);
            }}
          />
          {tex ? <pixiSprite texture={tex} width={node.width} height={node.height} /> : null}
          <pixiText
            text={node.label}
            x={node.width / 2}
            y={node.height / 2}
            anchor={0.5}
            style={{
              fontFamily: 'Inter',
              fontSize: 14,
              fill: '#052017',
              fontWeight: 'bold',
            }}
          />
        </pixiContainer>
      );
    }
  }
}
